/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import * as React from "react";

import { useAuth } from "@/providers";
import { useS3BucketIntegrations } from "@/shared/domain/integrations/hooks/useS3BucketIntegrations";
import { useAiCreditUsage } from "@/shared/domain/limits/hooks/useAiCreditUsage";
import { useLimits } from "@/shared/domain/limits/hooks/useLimits";
import { MeteringReport } from "@/shared/domain/metering";
import { useMetering } from "@/shared/domain/metering/hooks/useMetering";
import { Resource } from "@/types/resources";

import { AiCreditUsageCard } from "./components/AiCreditUsageCard";
import { SystemUsage } from "./components/SystemUsage";
import { MeteringVals } from "./components/types";
import { UsageBarChart } from "./components/UsageBarChart";
import { UsageLimitTriple } from "./components/UsageLimitTriple";

dayjs.extend(duration);
dayjs.extend(relativeTime);

function bytes_to_gigabytes(val: number) {
  return val / 1024 / 1024 / 1024;
}

function gigabytes_to_bytes(val: number) {
  return val * 1024 * 1024 * 1024;
}

const parseMeteringData = (
  meteringReportData: MeteringReport | MeteringReport[] | null,
): MeteringVals[] | undefined => {
  if (!meteringReportData) {
    return;
  }

  const mData: MeteringVals[] = [];

  const reports = Array.isArray(meteringReportData)
    ? meteringReportData
    : [meteringReportData];

  for (const report of [...reports].reverse()) {
    const date = new Date(report.end_time);

    const monthYr = date.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });

    let data_stored_gb = 0;
    let datasets_created = 0;
    let invocations_scheduled = 0;
    let data_egress_bandwidth_gb = 0;
    let data_upload_gb = 0;
    let action_run_time_hrs = 0;
    let action_compute_credits = 0;

    for (const resource of report.values) {
      switch (resource.resource_name) {
        case Resource.ManagedDataStoredBytes:
          data_stored_gb = bytes_to_gigabytes(resource.quantity);
          break;
        case Resource.DatasetsCreated:
          datasets_created = resource.quantity;
          break;
        case Resource.InvocationsScheduled:
          invocations_scheduled = resource.quantity;
          break;
        case Resource.ManagedDataDownloadBytes:
          data_egress_bandwidth_gb = bytes_to_gigabytes(resource.quantity);
          break;
        case Resource.ManagedDataUploadBytes:
          data_upload_gb = bytes_to_gigabytes(resource.quantity);
          break;
        case Resource.InvocationRuntimeMillis:
          action_run_time_hrs = resource.quantity / 1000 / 60 / 60;
          break;
        case Resource.InvocationComputeCreditMillionths:
          action_compute_credits = resource.quantity / 1_000_000;
          break;
      }
    }

    mData.push({
      name: monthYr,
      data_stored_gb,
      datasets_created,
      invocations_scheduled,
      data_egress_bandwidth_gb,
      data_upload_gb,
      action_run_time_hrs,
      action_compute_credits,
    });
  }

  return mData;
};

const getLatestDataStored = (report: MeteringVals[] | undefined) => {
  if (!report) {
    return;
  }
  const latestReport = report.at(-1);
  if (latestReport?.data_stored_gb) {
    return gigabytes_to_bytes(latestReport?.data_stored_gb);
  }
};

const getLatestDataDownloaded = (report: MeteringVals[] | undefined) => {
  if (!report) {
    return;
  }
  const latestReport = report.at(-1);
  if (latestReport?.data_egress_bandwidth_gb) {
    return gigabytes_to_bytes(latestReport?.data_egress_bandwidth_gb);
  }
};

const getLatestComputeCredits = (report: MeteringVals[] | undefined) => {
  if (!report) {
    return;
  }
  const latestReport = report.at(-1);
  if (latestReport?.action_compute_credits) {
    return latestReport?.action_compute_credits;
  }
};

export interface UsageProps {
  showGraphs: boolean;
}

export const Usage: React.FC<UsageProps> = ({ showGraphs }) => {
  const theme = useTheme();
  const { currentOrganization } = useAuth();

  const {
    limits: limitSummaryData,
    loading: limitSummaryLoading,
    error: limitSummaryError,
  } = useLimits();

  const {
    reports,
    loading: meteringReportLoading,
    error: meteringReportError,
  } = useMetering({ maxCount: 6 }); // 6 months

  const {
    usage: aiCreditUsage,
    loading: aiCreditUsageLoading,
    error: aiCreditUsageError,
  } = useAiCreditUsage();

  const {
    data: s3Buckets,
    isLoading: s3BucketsLoading,
    error: s3BucketsError,
  } = useS3BucketIntegrations(currentOrganization?.org_id);

  const hasByobBuckets = (s3Buckets?.length ?? 0) > 0;

  const parsedMeteringData = React.useMemo(() => {
    return parseMeteringData(reports);
  }, [reports]);

  const latestReport = React.useMemo(() => {
    if (!reports) {
      return null;
    }
    return Array.isArray(reports) ? reports[0] : reports;
  }, [reports]);

  const lastUpdated = React.useMemo(() => {
    if (!latestReport?.end_time) {
      return "Never";
    }

    return dayjs
      .duration(dayjs(latestReport.end_time).diff(dayjs()))
      .humanize(true);
  }, [latestReport]);

  let usageContent;

  if (
    meteringReportError ||
    limitSummaryError ||
    s3BucketsError ||
    aiCreditUsageError
  ) {
    usageContent = (
      <Box
        sx={{
          mt: theme.spacing(2),
          mb: theme.spacing(1),
        }}
      >
        {meteringReportError && (
          <Alert severity="error">{meteringReportError.message}</Alert>
        )}
        {limitSummaryError && (
          <Alert severity="error">{limitSummaryError.message}</Alert>
        )}
        {s3BucketsError && (
          <Alert severity="error">{s3BucketsError.message}</Alert>
        )}
        {aiCreditUsageError && (
          <Alert severity="error">{aiCreditUsageError.message}</Alert>
        )}
      </Box>
    );
  } else if (
    meteringReportLoading ||
    limitSummaryLoading ||
    s3BucketsLoading ||
    aiCreditUsageLoading
  ) {
    usageContent = (
      <Box
        sx={{
          mt: theme.spacing(5),
          mb: theme.spacing(3),
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress size="1.5rem" />
      </Box>
    );
  } else {
    usageContent = (
      <Box>
        {hasByobBuckets && (
          <Alert severity="info" sx={{ mb: theme.spacing(2) }}>
            Your organization stores data in your own cloud storage. The
            storage, bandwidth, and upload metrics below include only
            Roboto-managed data and exclude your own.
          </Alert>
        )}

        {showGraphs && (
          <Box
            sx={{
              mb: theme.spacing(2),
            }}
          >
            <Typography variant="body2">Last Updated: {lastUpdated}</Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, 400px)",
            gridTemplateRows: "repeat(auto-fit, 1fr)",
            columnGap: theme.spacing(2.5),
            rowGap: theme.spacing(2.5),
          }}
        >
          <UsageLimitTriple
            limitSummaryData={limitSummaryData}
            currentDataStored={getLatestDataStored(parsedMeteringData) || 0}
            currentDataDownloaded={
              getLatestDataDownloaded(parsedMeteringData) || 0
            }
            currentComputeCredits={
              getLatestComputeCredits(parsedMeteringData) || 0
            }
          />

          {/*
            `useAiCreditUsage` returns `AiCreditUsage | null`; the truthiness
            check narrows for the non-null `usage` prop and covers the brief
            window during an org switch when `usage` is reset to `null`.
          */}
          {aiCreditUsage && <AiCreditUsageCard usage={aiCreditUsage} />}

          {showGraphs && (
            <>
              <UsageBarChart
                title="Data Stored"
                subtitle="Data stored in GB for a given month"
                data={parsedMeteringData ?? []}
                dataKey="data_stored_gb"
                allowYDecimals={false}
                fill="#94a3b8"
                yUnit=" GB"
              />
              <UsageBarChart
                title="Data Bandwidth"
                subtitle="Data egressed in GB for a given month"
                data={parsedMeteringData ?? []}
                dataKey="data_egress_bandwidth_gb"
                fill="#14b8a6"
                allowYDecimals={false}
                yUnit=" GB"
              />
              <UsageBarChart
                title="Processing Units"
                subtitle="Processing units (RPU) used per month"
                data={parsedMeteringData ?? []}
                dataKey="action_compute_credits"
                allowYDecimals={false}
                fill="#f43f5e"
              />
              <UsageBarChart
                title="Datasets Created"
                subtitle="Datasets created for a given month"
                data={parsedMeteringData ?? []}
                dataKey="datasets_created"
                allowYDecimals={false}
                fill="#d97706"
              />
              <UsageBarChart
                title="Data Uploaded"
                subtitle="Data uploaded in GB for a given month"
                data={parsedMeteringData ?? []}
                dataKey="data_upload_gb"
                fill="#3b82f6"
                allowYDecimals={false}
                yUnit=" GB"
              />
              <UsageBarChart
                title="Actions Invoked"
                subtitle="Number of actions run for a given month"
                data={parsedMeteringData ?? []}
                dataKey="invocations_scheduled"
                allowYDecimals={false}
                fill="#16a34a"
              />
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box>{usageContent}</Box>
      {showGraphs && <SystemUsage latestReport={latestReport} />}
    </>
  );
};
